import * as Sentry from '@sentry/react';
import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';

import {
  HStack,
  Icon,
  NewButton,
  Paragraph,
  TooltipProvider,
  VStack,
  useToast,
} from '@eluve/components';
import { useNamedLogger } from '@eluve/logger';
import { useCaptureEvent } from '@eluve/posthog-react';
import { bytesToMB } from '@eluve/utils';

import { useBrowserMemoryReporting } from './utils/useBrowserMemoryReporting';
import { useTabLifecycleReporting } from './utils/useTabLifecycleReporting';

const DEVICE_STATS_REPORTING_INTERVAL = 5 * 60 * 1000;

export const AppLayout: React.FC = () => {
  const logger = useNamedLogger('App');
  const [toastOpen, setToastOpen] = useState<boolean>(false);
  const captureEvent = useCaptureEvent();
  const toast = useToast();

  useBrowserMemoryReporting(setToastOpen, DEVICE_STATS_REPORTING_INTERVAL);
  useTabLifecycleReporting();

  useEffect(
    function reportStorageEstimate() {
      const interval = setInterval(async () => {
        try {
          if (navigator.storage.estimate) {
            const { usage, quota } = await navigator.storage.estimate();

            logger.info('Storage estimate', {
              usage: usage ? bytesToMB(usage) : undefined,
              quota: quota ? bytesToMB(quota) : undefined,
            });
          }
        } catch (e) {
          Sentry.captureException(e);
        }
      }, DEVICE_STATS_REPORTING_INTERVAL);

      return () => clearInterval(interval);
    },
    [logger],
  );

  useEffect(
    function displayToast() {
      if (!toastOpen) {
        return;
      }
      toast.message(
        <HStack align="start" gap={3}>
          <Icon name="RotateCw" size="sm" />
          <VStack gap={0}>
            <Paragraph size="md" weight="semibold">
              High Memory Usage
            </Paragraph>
            <Paragraph className="text-balance" size="md" weight="regular">
              Things might run smoother with a quick page reload. Care to try?
            </Paragraph>
            <HStack className="mb-1 mt-3">
              <NewButton
                onClick={() => {
                  captureEvent('user_reloaded_for_high_memory', undefined, {
                    send_instantly: true,
                  });
                  setTimeout(() => {
                    window.location.reload();
                  }, 50);
                }}
                size="s"
                text="Reload Now"
              />
              <NewButton
                onClick={() => toast.dismiss()}
                size="s"
                text="Dismiss"
                type="outline"
              />
            </HStack>
          </VStack>
        </HStack>,
        {
          id: 'high-memory-toast',
          position: 'bottom-right',
          duration: Infinity,
          onDismiss: () => {
            setToastOpen(false);
          },
        },
      );
    },
    [toastOpen, setToastOpen, captureEvent, toast],
  );

  return (
    <TooltipProvider delayDuration={0}>
      <Outlet />
    </TooltipProvider>
  );
};
